import { Modal } from './Modal';
const EDUCATIONAL_MATERIAL_TITLE = 'Educational Material';
const PRIVACY_PRACTICES_TITLE = 'Notice of Privacy Practices';
const PRIVACY_PRACTICES_SUBTITLE = 'Tapestry TeleHealth, PLLC; Chess Medicine, PLLC; Chess Medical, PC';
const LOGO_URL = 'https://tapestryhealth.com/wp-content/uploads/2025/06/Tapestry-Health-Logo-01.svg';
const EDUCATIONAL_IMAGE_URL = 'https://tapestryhealth.com/wp-content/uploads/2026/07/educational-material.png';
const MONITORING_VIDEO_URL = 'https://tapestryhealth.com/wp-content/uploads/2026/07/video-of-monitoring.mp4';
const SAMPLE_DAILY_RISK_REPORT_URL = 'https://tapestryhealth.com/wp-content/uploads/2026/07/daily-risk-report.pdf';
const SAMPLE_CCM_REPORT_URL = 'https://tapestryhealth.com/wp-content/uploads/2026/07/sample-ccm-report.pdf';
function EducationalMaterial({ facilityName, onPlayVideo }) {
  return (
    <div className="tcf-prose">
      <img className="tcf-prose-logo" src={LOGO_URL} alt="TapestryHealth" />
      <p className="tcf-prose-eyebrow">A partner of {facilityName}</p>
      <p>
        {facilityName} has partnered with TapestryHealth™ to assist their team in providing the highest quality and most
        timely care to its residents. TapestryHealth™ uses the latest technology and skilled healthcare providers to
        proactively identify when a resident’s health starts to decline. We help to create a plan of care to avoid
        hospitalizations and emergency room visits or to get residents to the hospital faster, when necessary.
      </p>
      <p>
        With TapestryHealth™, your care team knows how each patient is doing every day. They know who needs more care or
        supervision and who may need adjustments to their care plan. Our goal is to provide the care that each resident
        needs by identifying and treating problems earlier rather than having to go the hospital after a patient’s
        condition has worsened.
      </p>
      <p>
        TapestryHealth™ provides facilities with a safety net of solutions that integrate seamlessly with existing
        workflows to create a personalized, proactive approach for each patient. These solutions may include Daily Risk
        Reports, Chronic Care Management Reports, and Remote Physiologic Monitoring. These services are approved by the
        Centers for Medicare and Medicaid (CMS) to meet the complex health care needs of patients with multiple chronic
        conditions.
      </p>

      <h3>Chronic Care Management (CCM)</h3>
      <p>
        Chronic Care Management provides facilities with a Daily Risk Report that highlights the residents that are most
        at-risk of experiencing an acute event. It also includes a monthly Chronic Care Management report that assesses
        medications, changes in vital signs, changes to daily activities, and other health status indicators. Each
        patient’s monthly report also has a detailed care plan that is updated and followed up on as health status
        changes.
      </p>
      <p className="tcf-prose-links">
        <a className="tcf-link tcf-link--teal" href={SAMPLE_DAILY_RISK_REPORT_URL} target="_blank" rel="noreferrer">
          Sample Daily Risk Report
        </a>
        <a className="tcf-link tcf-link--teal" href={SAMPLE_CCM_REPORT_URL} target="_blank" rel="noreferrer">
          Sample CCM Report
        </a>
      </p>

      <h3>Remote Physiologic Monitoring (RPM)</h3>
      <p>
        Remote Physiologic Monitoring collects and reports vital signs, such as pulse and respiration rate, that are
        early indicators of declining health status – in real time. Changes in vital signs that indicate a decline in
        health status create notifications to prompt action by the on-site care team. Over time, Remote Physiologic
        Monitoring picks up small – but important – changes in vital signs which often go unnoticed during routine
        rounds and doctor visits. These changes can indicate that a resident is starting to develop signs of a condition
        that could worsen. We help {facilityName} treat patients faster and onsite, when possible, so that ED visits and
        hospitalizations can be avoided – or if hospitalization is needed, the problem is identified earlier.
      </p>
      <figure className="tcf-prose-media">
        <img src={EDUCATIONAL_IMAGE_URL} alt="TapestryHealth™ remote monitoring in a care facility" />
        <figcaption>
          <button type="button" className="tcf-prose-video-caption" onClick={onPlayVideo}>
            Video of Monitoring
          </button>
        </figcaption>
      </figure>
      <p>
        TapestryHealth™’s technology does not replace physicians, nurses, or anyone else at your skilled nursing center.
        It helps the busy caregivers by providing additional support through data-driven, proactive, care management and
        coordination so {facilityName} residents enjoy continued health and quality of life.
      </p>
      <p>
        Want to learn more about us? Check out{' '}
        <a href="https://www.tapestryhealth.com" target="_blank" rel="noreferrer">
          www.tapestryhealth.com
        </a>
      </p>
      <p className="tcf-prose-note">
        <strong>Disclaimer:</strong> Not every facility is contracted for all services listed. To learn more about which
        services your facility is contracted for, reach out with your facility administrator.
      </p>
    </div>
  );
}
function PrivacyPractices() {
  return (
    <div className="tcf-prose">
      <p className="tcf-prose-entities">{PRIVACY_PRACTICES_SUBTITLE}</p>
      <p className="tcf-prose-muted">{PRIVACY_PRACTICES_TITLE}</p>
      <hr className="tcf-prose-rule" />
      <p className="tcf-prose-muted">
        This notice describes how medical information about you may be used and disclosed and how you can get access to
        this information. Please review it carefully.
      </p>
      <h3>Your Information. Your Rights. Our Responsibilities.</h3>
      <h3>When it comes to your health information, you have certain rights.</h3>
      <p>This section explains your rights and some of our responsibilities to help you.</p>

      <h3>Get an electronic or paper copy of your medical record</h3>
      <p>
        You can ask to see or get an electronic or paper copy of your medical record and other health information we
        have about you. Ask us how to do this. We will provide a copy or a summary of your health information, usually
        within 30 days of your request. We may charge a reasonable, cost-based fee.
      </p>

      <h3>Ask us to correct your medical record</h3>
      <p>
        You can ask us to correct health information about you that you think is incorrect or incomplete. Ask us how to
        do this. We may say “no” to your request, but we’ll tell you why in writing within 60 days.
      </p>

      <h3>Request confidential communications</h3>
      <p>
        You can ask us to contact you in a specific way (for example, home or office phone) or to send mail to a
        different address. We will say “yes” to all reasonable requests.
      </p>

      <h3>Ask us to limit what we use or share</h3>
      <p>
        You can ask us not to use or share certain health information for treatment, payment, or our operations. We are
        not required to agree to your request, and we may say “no” if it would affect your care. If you pay for a
        service or health care item out-of-pocket in full, you can ask us not to share that information for the purpose
        of payment or our operations with your health insurer. We will say “yes” unless a law requires us to share that
        information.
      </p>

      <h3>Get a list of those with whom we’ve shared information</h3>
      <p>
        You can ask for a list (accounting) of the times we’ve shared your health information for six years prior to the
        date you ask, who we shared it with, and why. We will include all the disclosures except for those about
        treatment, payment, and health care operations, and certain other disclosures (such as any you asked us to
        make). We’ll provide one accounting a year for free but will charge a reasonable, cost-based fee if you ask for
        another one within 12 months.
      </p>

      <h3>Get a copy of this privacy notice</h3>
      <p>
        You can ask for a paper copy of this notice at any time, even if you have agreed to receive the notice
        electronically. We will provide you with a paper copy promptly.
      </p>

      <h3>Choose someone to act for you</h3>
      <p>
        If you have given someone medical power of attorney or if someone is your legal guardian, that person can
        exercise your rights and make choices about your health information. We will make sure the person has this
        authority and can act for you before we take any action.
      </p>

      <h3>File a complaint if you feel your rights are violated</h3>
      <p>
        You can complain if you feel we have violated your rights by contacting us using the information on the back
        page. You can file a complaint with the U.S. Department of Health and Human Services Office for Civil Rights by
        sending a letter to 200 Independence Avenue, S.W., Washington, D.C. 20201, calling 1-877-696-6775, or visiting{' '}
        <a href="https://www.hhs.gov/ocr/privacy/hipaa/complaints/" target="_blank" rel="noreferrer">
          www.hhs.gov/ocr/privacy/hipaa/complaints/
        </a>
        . We will not retaliate against you for filing a complaint.
      </p>

      <h3>For certain health information, you can tell us your choices about what we share.</h3>
      <p>
        If you have a clear preference for how we share your information in the situations described below, talk to us.
        Tell us what you want us to do, and we will follow your instructions.
      </p>
      <ul>
        <li>Share information with your family, close friends, or others involved in your care</li>
        <li>Share information in a disaster relief situation</li>
        <li>Include your information in a directory</li>
      </ul>
      <p>
        If you are not able to tell us your preference, for example if you are unconscious, we may go ahead and share
        your information if we believe it is in your best interest. We may also share your information when needed to
        lessen a serious and imminent threat to health or safety.
      </p>
      <p>
        <strong>In these cases we never share your information unless you give us written permission:</strong>
      </p>
      <ul>
        <li>Marketing purposes</li>
        <li>Sale of your information</li>
        <li>Most sharing of psychotherapy notes</li>
      </ul>

      <h3>How do we typically use or share your health information?</h3>
      <h3>Treat you</h3>
      <p>
        We can use your health information and share it with other professionals who are treating you. Example: A doctor
        treating you for an injury asks another doctor about your overall health condition.
      </p>
      <h3>Run our organization</h3>
      <p>
        We can use and share your health information to run our practice, improve your care, and contact you when
        necessary. Example: We use health information about you to manage your treatment and services.
      </p>
      <h3>Bill for your services</h3>
      <p>
        We can use and share your health information to bill and get payment from health plans or other entities.
        Example: We give information about you to your health insurance plan so it will pay for your services.
      </p>
      <p className="tcf-prose-muted">continued on next page</p>

      <h3>How else can we use or share your health information?</h3>
      <p>
        We are allowed or required to share your information in other ways, usually in ways that contribute to the
        public good, such as public health and research. We have to meet many conditions in the law before we can share
        your information for these purposes. For more information see:{' '}
        <a
          href="https://www.hhs.gov/ocr/privacy/hipaa/understanding/consumers/index.html"
          target="_blank"
          rel="noreferrer"
        >
          www.hhs.gov/ocr/privacy/hipaa/understanding/consumers/index.html
        </a>
        .
      </p>
      <ul>
        <li>Preventing disease</li>
        <li>Helping with product recalls</li>
        <li>Reporting adverse reactions to medications</li>
        <li>Reporting suspected abuse, neglect, or domestic violence</li>
        <li>Preventing or reducing a serious threat to anyone’s health or safety</li>
      </ul>
      <h3>Do research</h3>
      <p>We can use or share your information for health research.</p>
      <h3>Comply with the law</h3>
      <p>
        We will share information about you if state or federal laws require it, including with the Department of Health
        and Human Services if it wants to see that we’re complying with federal privacy law.
      </p>
      <h3>Respond to organ and tissue donation requests</h3>
      <p>We can share health information about you with organ procurement organizations.</p>
      <h3>Work with a medical examiner or funeral director</h3>
      <p>
        We can share health information with a coroner, medical examiner, or funeral director when an individual dies.
      </p>
      <h3>Address workers’ compensation, law enforcement, and other government requests</h3>
      <p>We can use or share health information about you:</p>
      <ul>
        <li>For workers’ compensation claims</li>
        <li>For law enforcement purposes or with a law enforcement official</li>
        <li>With health oversight agencies for activities authorized by law</li>
        <li>
          For special government functions such as military, national security, and presidential protective services
        </li>
      </ul>
      <h3>Respond to lawsuits and legal actions</h3>
      <p>
        We can share health information about you in response to a court or administrative order, or in response to a
        subpoena.
      </p>
      <p>
        Certain health information may be subject to more stringent protections, such as under Part 2 (substance abuse
        regulations) or under applicable state law. Please note that (i) Tapestry TeleHealth, PLLC, (ii) Chess Medicine,
        PLLC, and (iii) Chess Medical, PC are third party health care providers. We do not create or maintain a facility
        directory. You may receive a separate HIPAA Notice of Privacy Practices from a facility, such as a nursing home,
        rehabilitation facility, or nursing facility. Those facilities will have their own HIPAA Notice of Privacy
        Practices, which govern their own use and disclosure of patients’ health information. Sometimes we provide
        services as a vendor to other organizations. Other times, we provide services as a health care provider,
        including but not limited to patient care services, monitoring, and chronic care. Individuals might receive some
        or all of these services as appropriate.
      </p>

      <hr className="tcf-prose-rule" />
      <p>
        We are required by law to maintain the privacy and security of your protected health information. We will let
        you know promptly if a breach occurs that may have compromised the privacy or security of your information. We
        must follow the duties and privacy practices described in this notice and give you a copy of it. We will not use
        or share your information other than as described here unless you tell us we can in writing. If you tell us we
        can, you may change your mind at any time. Let us know in writing if you change your mind.
      </p>
      <p>
        For more information see:{' '}
        <a
          href="https://www.hhs.gov/ocr/privacy/hipaa/understanding/consumers/noticepp.html"
          target="_blank"
          rel="noreferrer"
        >
          www.hhs.gov/ocr/privacy/hipaa/understanding/consumers/noticepp.html
        </a>
        .
      </p>

      <h3>Changes to the Terms of This Notice</h3>
      <p>
        We can change the terms of this notice, and the changes will apply to all information we have about you. The new
        notice will be available upon request, in our office, and on our web site.
      </p>

      <h3>This Notice of Privacy Practices applies to the following organizations.</h3>
      <p>
        For purposes of HIPAA, Tapestry TeleHealth, PLLC, Chess Medicine, PLLC, and Chess Medical, PC are an Affiliated
        Covered Entity (ACE) and are also an Organized Health Care Arrangement (OHCA) for certain joint activities, such
        as treatment, payment and health care operations. These entities have agreed to a joint HIPAA Notice of Privacy
        Practices.
      </p>
      <p>
        To exercise your HIPAA rights or make a complaint, please contact Tapestry Health’s HIPAA Privacy Officer at{' '}
        <a href="mailto:compliance@tapestryhealth.com">compliance@tapestryhealth.com</a>, by phone at{' '}
        <a href="tel:2036668145">203-666-8145</a>, or HIPAA Privacy Officer, 99 Hawley Lane, Stratford, CT 06614.
      </p>
    </div>
  );
}
function VideoLightbox({ open, onClose }) {
  return (
    <Modal open={open} onClose={onClose} title="Video of Monitoring" variant="video">
      {open && (
        <video className="tcf-video" controls autoPlay playsInline src={MONITORING_VIDEO_URL}>
          Your browser can’t play this video.{' '}
          <a href={MONITORING_VIDEO_URL} target="_blank" rel="noreferrer">
            Download the video of monitoring
          </a>
          .
        </video>
      )}
    </Modal>
  );
}
export {
  EDUCATIONAL_IMAGE_URL,
  EDUCATIONAL_MATERIAL_TITLE,
  EducationalMaterial,
  LOGO_URL,
  MONITORING_VIDEO_URL,
  PRIVACY_PRACTICES_SUBTITLE,
  PRIVACY_PRACTICES_TITLE,
  PrivacyPractices,
  SAMPLE_CCM_REPORT_URL,
  SAMPLE_DAILY_RISK_REPORT_URL,
  VideoLightbox,
};
